import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveZoneArea,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  parseCofFromFieldValues,
  parseConfidenceFromFieldValues,
  parseZonesFromFieldValues,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type {
  LibraTicket,
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
} from 'features/libra/ticket/libraTicketsApi'
import { ELIGIBILITY_STATUS } from 'features/libra/types/eligibility'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

const UNKNOWN_SITE_KEY = '__unknown_site__'

/**
 * NII Screening confidence field id. Hardcoded (matching the by-zone drilldown
 * table) because the ROI panel has no ZoneAnalysisConfig; every NII ticket uses
 * this field to drive coverage confidence.
 */
const NII_CONFIDENCE_FIELD_NAME =
  'confidenceInPredictingDegradationTypeAndLocation'

export const INSPECTABLE_AREA_M2_FIELD = 'inspectable_area_m2'
export const NII_ROI_EQUIPMENT_PREP_AED_FIELD =
  'nii_roi_equipment_prep_mob_demob_aed'
export const NII_ROI_INSPECTION_AED_FIELD = 'nii_roi_inspection_aed'
export const NII_ROI_SCAFFOLDING_AED_FIELD = 'nii_roi_scaffolding_aed'

export type NiiCalculatorSavingsCategory =
  | 'equipment'
  | 'inspection'
  | 'scaffolding'
  | 'hse'
  | 'production'
  | 'extension'

export type NiiCalculatorSavingsBreakdown = Record<
  NiiCalculatorSavingsCategory,
  number
> & {
  total: number
}

export type ResolvedNiiCalculatorAssumptions = {
  traditionalInspectionTeamPerAsset: number
  equipmentPrepMobDemobAedPerAsset: number
  traditionalInspectionAedPerAsset: number
  niiInspectionAedPerAsset: number
  scaffoldingAedPerAsset: number
  /** NII inspection rate, USD per m², applied to blended NII area. */
  niiInspectionRateUsdPerM2: number
  /** Internal inspection rate, USD per m², applied to approximate vessel size. */
  internalInspectionRateUsdPerM2: number
  /** FX rate, AED per USD, used with the per-m² inspection rates. */
  fxRateAedPerUsd: number
  hseIncidentProbPerPersonPerTar: number
  hseDownsideAed: number
  criticalPathReductionPercent: number
  /** Extension avoidance: baseline probability the TAR extends (percentage points). */
  extensionProbBaselinePercent: number
  /** Extension avoidance: probability the TAR extends with NII (percentage points). */
  extensionProbWithNiiPercent: number
  /** Extension avoidance: expected extension length in days. */
  expectedExtensionDays: number
  /** Extension avoidance: confidence findings avoid the extension (percentage points). */
  discoveryConfidencePercent: number
  tarDurationDays: number
  dailyProductionAed: number
  inspectionOnCriticalPath: boolean
}

export const DEFAULT_RESOLVED_NII_CALCULATOR_ASSUMPTIONS: ResolvedNiiCalculatorAssumptions =
  {
    traditionalInspectionTeamPerAsset: 8,
    equipmentPrepMobDemobAedPerAsset: 200_000,
    traditionalInspectionAedPerAsset: 15_000,
    niiInspectionAedPerAsset: 6_119,
    scaffoldingAedPerAsset: 183_625,
    niiInspectionRateUsdPerM2: 8,
    internalInspectionRateUsdPerM2: 10,
    fxRateAedPerUsd: 3.6725,
    hseIncidentProbPerPersonPerTar: 0.001,
    hseDownsideAed: 1_500_000,
    criticalPathReductionPercent: 5,
    extensionProbBaselinePercent: 25,
    extensionProbWithNiiPercent: 5,
    expectedExtensionDays: 7,
    discoveryConfidencePercent: 50,
    tarDurationDays: 0,
    dailyProductionAed: 0,
    inspectionOnCriticalPath: false,
  }

export const NII_CALCULATOR_CATEGORY_COLORS: Record<
  NiiCalculatorSavingsCategory,
  string
> = {
  equipment: '#4472C4',
  inspection: '#5B9BD5',
  scaffolding: '#ED7D31',
  hse: '#A78BFA',
  production: '#70AD47',
  extension: '#E15759',
}

export const NII_CALCULATOR_CATEGORY_LABELS: Record<
  NiiCalculatorSavingsCategory,
  string
> = {
  equipment: 'Equip prep / mob / demob',
  inspection: 'Inspection',
  scaffolding: 'Scaffolding',
  hse: 'HSE',
  production: 'Production',
  extension: 'Extension avoidance',
}

export type NiiRoiAssetOverrideFields = {
  equipmentPrepMobDemobAedPerAsset: number | undefined
  niiInspectionAedPerAsset: number | undefined
  scaffoldingAedPerAsset: number | undefined
}

export type NiiRoiAssetCostInputs = {
  equipmentPrepMobDemobAedPerAsset: number
  traditionalInspectionAedPerAsset: number
  niiInspectionAedPerAsset: number
  scaffoldingAedPerAsset: number
  /** Total NII (coverage) area inspected on the asset, m². Cantilever-derived. */
  blendedNiiAreaM2: number
  /** Approximate total vessel surface area across the asset's zones, m². */
  vesselSizeM2: number
}

export type NiiRoiTicketAreas = {
  blendedNiiAreaM2: number
  vesselSizeM2: number
}

export function formatAedFull(value: number): string {
  if (value === 0 || Number.isNaN(value)) {
    return 'AED 0'
  }
  const rounded = Math.round(value * 100) / 100
  return `AED ${rounded.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
}

type SiteRoiMap =
  | Readonly<Record<string, SiteRoiAssumptionsValue | undefined>>
  | undefined

export function getNiiCalculatorTarRoiBySiteFromGroup(
  group: LibraTicketGroup
): SiteRoiMap {
  return group[TAR_ROI_GROUP_DATA_KEY]
}

function storedHseProb(
  stored: SiteRoiAssumptionsValue | undefined
): number | undefined {
  return (
    stored?.hseIncidentProbPerPersonPerTar ??
    stored?.hseIncidentProbPerPersonPerDay
  )
}

function pickNumber(
  siteStored: SiteRoiAssumptionsValue | undefined,
  globalStored: SiteRoiAssumptionsValue | undefined,
  key: keyof SiteRoiAssumptionsValue,
  fallback: number
): number {
  const siteValue = siteStored?.[key]
  if (typeof siteValue === 'number') return siteValue
  const globalValue = globalStored?.[key]
  if (typeof globalValue === 'number') return globalValue
  return fallback
}

export function mergeNiiCalculatorAssumptionsForSite(
  globalStored: SiteRoiAssumptionsValue | undefined,
  siteStored: SiteRoiAssumptionsValue | undefined
): ResolvedNiiCalculatorAssumptions {
  const d = DEFAULT_RESOLVED_NII_CALCULATOR_ASSUMPTIONS
  return {
    traditionalInspectionTeamPerAsset: pickNumber(
      siteStored,
      globalStored,
      'traditionalInspectionTeamPerAsset',
      d.traditionalInspectionTeamPerAsset
    ),
    equipmentPrepMobDemobAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'equipmentPrepMobDemobAedPerAsset',
      d.equipmentPrepMobDemobAedPerAsset
    ),
    traditionalInspectionAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'traditionalInspectionAedPerAsset',
      d.traditionalInspectionAedPerAsset
    ),
    niiInspectionAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'niiInspectionAedPerAsset',
      d.niiInspectionAedPerAsset
    ),
    scaffoldingAedPerAsset: pickNumber(
      siteStored,
      globalStored,
      'scaffoldingAedPerAsset',
      d.scaffoldingAedPerAsset
    ),
    niiInspectionRateUsdPerM2: pickNumber(
      siteStored,
      globalStored,
      'niiInspectionRateUsdPerM2',
      d.niiInspectionRateUsdPerM2
    ),
    internalInspectionRateUsdPerM2: pickNumber(
      siteStored,
      globalStored,
      'internalInspectionRateUsdPerM2',
      d.internalInspectionRateUsdPerM2
    ),
    fxRateAedPerUsd: pickNumber(
      siteStored,
      globalStored,
      'fxRateAedPerUsd',
      d.fxRateAedPerUsd
    ),
    extensionProbBaselinePercent: pickNumber(
      siteStored,
      globalStored,
      'extensionProbBaselinePercent',
      d.extensionProbBaselinePercent
    ),
    extensionProbWithNiiPercent: pickNumber(
      siteStored,
      globalStored,
      'extensionProbWithNiiPercent',
      d.extensionProbWithNiiPercent
    ),
    expectedExtensionDays: pickNumber(
      siteStored,
      globalStored,
      'expectedExtensionDays',
      d.expectedExtensionDays
    ),
    discoveryConfidencePercent: pickNumber(
      siteStored,
      globalStored,
      'discoveryConfidencePercent',
      d.discoveryConfidencePercent
    ),
    hseIncidentProbPerPersonPerTar:
      storedHseProb(siteStored) ??
      storedHseProb(globalStored) ??
      d.hseIncidentProbPerPersonPerTar,
    hseDownsideAed: pickNumber(
      siteStored,
      globalStored,
      'hseDownsideAed',
      d.hseDownsideAed
    ),
    criticalPathReductionPercent: pickNumber(
      siteStored,
      globalStored,
      'criticalPathReductionPercent',
      d.criticalPathReductionPercent
    ),
    tarDurationDays: pickNumber(
      siteStored,
      undefined,
      'tarDurationDays',
      d.tarDurationDays
    ),
    dailyProductionAed: pickNumber(
      siteStored,
      undefined,
      'dailyProductionAed',
      d.dailyProductionAed
    ),
    inspectionOnCriticalPath:
      siteStored?.inspectionOnCriticalPath ?? d.inspectionOnCriticalPath,
  }
}

export function getTicketAreaM2(ticket: LibraTicket): number {
  return (
    unwrapFieldValue<number>(ticket.fieldValues[INSPECTABLE_AREA_M2_FIELD]) ?? 0
  )
}

/**
 * Per-asset areas derived from the ticket's zone analysis (the same coverage
 * math Cantilever renders in-app):
 * - `blendedNiiAreaM2` = Σ required coverage area across zones (scanned NII area)
 * - `vesselSizeM2` = Σ effective total zone area (approximate vessel surface)
 *
 * Returns zeros when the ticket has no usable zone data, so callers can fall
 * back to flat per-asset inspection costs.
 */
export function computeNiiRoiTicketAreas(
  ticket: LibraTicket
): NiiRoiTicketAreas {
  const fieldValues = ticket.fieldValues ?? {}
  const { zones } = parseZonesFromFieldValues(fieldValues)
  if (zones.length === 0) return { blendedNiiAreaM2: 0, vesselSizeM2: 0 }

  const { cof } = parseCofFromFieldValues(fieldValues)
  const confidence = parseConfidenceFromFieldValues(
    fieldValues,
    NII_CONFIDENCE_FIELD_NAME
  )
  const cofModifier = getCofModifier(cof.level)

  let blendedNiiAreaM2 = 0
  let vesselSizeM2 = 0
  for (const zone of zones) {
    vesselSizeM2 += getEffectiveZoneArea(zone)
    const coverage = calculateRequiredCoverage(zone, cofModifier, confidence)
    blendedNiiAreaM2 += coverage?.required_coverage_area_sq_m ?? 0
  }
  return { blendedNiiAreaM2, vesselSizeM2 }
}

/** NII inspection cost from blended NII area × USD/m² rate × FX. */
function niiInspectionCostFromArea(
  blendedNiiAreaM2: number,
  a: ResolvedNiiCalculatorAssumptions
): number {
  return blendedNiiAreaM2 * a.niiInspectionRateUsdPerM2 * a.fxRateAedPerUsd
}

/** Traditional inspection cost from vessel size × USD/m² internal rate × FX. */
function traditionalInspectionCostFromArea(
  vesselSizeM2: number,
  a: ResolvedNiiCalculatorAssumptions
): number {
  return vesselSizeM2 * a.internalInspectionRateUsdPerM2 * a.fxRateAedPerUsd
}

export function readNiiRoiAssetOverrides(
  ticket: LibraTicket
): NiiRoiAssetOverrideFields {
  const equipmentPrepMobDemobAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_EQUIPMENT_PREP_AED_FIELD]
  )
  const niiInspectionAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_INSPECTION_AED_FIELD]
  )
  const scaffoldingAedPerAsset = unwrapFieldValue<number | null>(
    ticket.fieldValues[NII_ROI_SCAFFOLDING_AED_FIELD]
  )
  return {
    equipmentPrepMobDemobAedPerAsset:
      typeof equipmentPrepMobDemobAedPerAsset === 'number'
        ? equipmentPrepMobDemobAedPerAsset
        : undefined,
    niiInspectionAedPerAsset:
      typeof niiInspectionAedPerAsset === 'number'
        ? niiInspectionAedPerAsset
        : undefined,
    scaffoldingAedPerAsset:
      typeof scaffoldingAedPerAsset === 'number'
        ? scaffoldingAedPerAsset
        : undefined,
  }
}

export function resolveNiiRoiAssetCostInputs(
  ticket: LibraTicket,
  assumptions: ResolvedNiiCalculatorAssumptions
): NiiRoiAssetCostInputs {
  const overrides = readNiiRoiAssetOverrides(ticket)
  const { blendedNiiAreaM2, vesselSizeM2 } = computeNiiRoiTicketAreas(ticket)

  // NII cost: explicit per-asset override wins, then the rate × blended NII
  // area, then the flat per-asset fallback when no coverage area is available.
  const niiInspectionAedPerAsset =
    overrides.niiInspectionAedPerAsset ??
    (blendedNiiAreaM2 > 0
      ? niiInspectionCostFromArea(blendedNiiAreaM2, assumptions)
      : assumptions.niiInspectionAedPerAsset)

  // Traditional cost: rate × approximate vessel size, else the flat fallback.
  const traditionalInspectionAedPerAsset =
    vesselSizeM2 > 0
      ? traditionalInspectionCostFromArea(vesselSizeM2, assumptions)
      : assumptions.traditionalInspectionAedPerAsset

  return {
    equipmentPrepMobDemobAedPerAsset:
      overrides.equipmentPrepMobDemobAedPerAsset ??
      assumptions.equipmentPrepMobDemobAedPerAsset,
    traditionalInspectionAedPerAsset,
    niiInspectionAedPerAsset,
    scaffoldingAedPerAsset:
      overrides.scaffoldingAedPerAsset ?? assumptions.scaffoldingAedPerAsset,
    blendedNiiAreaM2,
    vesselSizeM2,
  }
}

function isNiiEligibleForRoi(ticket: LibraTicket): boolean {
  if (!ticket.workflowComplete) return false
  return ticket.fieldValues?.eligibility?.value === ELIGIBILITY_STATUS.ELIGIBLE
}

function pickRoiEligibleTickets(
  tickets: ReadonlyArray<LibraTicket>
): LibraTicket[] {
  return tickets.filter(isNiiEligibleForRoi)
}

function ticketUnitKey(ticket: LibraTicket): string {
  return typeof ticket.tags.unit === 'string' && ticket.tags.unit
    ? ticket.tags.unit
    : ticket.id
}

function ticketSiteKey(ticket: LibraTicket): string {
  return typeof ticket.tags.site === 'string' && ticket.tags.site
    ? ticket.tags.site
    : UNKNOWN_SITE_KEY
}

function countUniqueUnits(tickets: ReadonlyArray<LibraTicket>): number {
  return uniq(tickets.map(ticketUnitKey)).length
}

function isTicketCompleteForCalculator(ticket: LibraTicket): boolean {
  return ticket.workflowComplete || ticket.ticketStatus === 'approved'
}

function isTicketInProgressForCalculator(ticket: LibraTicket): boolean {
  const status = ticket.ticketStatus.trim().toLowerCase()
  return status === 'pending_review' || status === 'in_progress'
}

export type NiiCalculatorSiteSummary = {
  siteId: string
  reviewedAssets: number
  eligibleAssets: number
  notStartedAssets: number
  inProgressAssets: number
  completeAssets: number
  savingPerEligibleAsset: number
  assumptions: ResolvedNiiCalculatorAssumptions
  breakdown: NiiCalculatorSavingsBreakdown
}

export type NiiCalculatorAggregate = {
  byCategory: NiiCalculatorSavingsBreakdown
  reviewedAssets: number
  eligibleAssets: number
  notStartedAssets: number
  inProgressAssets: number
  completeAssets: number
  savingPerEligibleAsset: number
  bySite: ReadonlyArray<NiiCalculatorSiteSummary>
}

function emptyCalculatorBreakdown(): NiiCalculatorSavingsBreakdown {
  return {
    equipment: 0,
    inspection: 0,
    scaffolding: 0,
    hse: 0,
    production: 0,
    extension: 0,
    total: 0,
  }
}

/**
 * Extension avoidance saving (TAR-level, applied once per turnaround):
 * (P_baseline − P_with_NII) × expected extension days × discovery confidence ×
 * daily production. Independent of the critical-path flag.
 */
export function computeExtensionAvoidanceSaving(
  a: ResolvedNiiCalculatorAssumptions
): number {
  const probDelta =
    (a.extensionProbBaselinePercent - a.extensionProbWithNiiPercent) / 100
  const daysSaved = probDelta * a.expectedExtensionDays
  return daysSaved * (a.discoveryConfidencePercent / 100) * a.dailyProductionAed
}

export function computeNiiCalculatorSavingsForSite(
  tickets: ReadonlyArray<LibraTicket>,
  assumptions: ResolvedNiiCalculatorAssumptions
): NiiCalculatorSiteSummary {
  const roiTickets = pickRoiEligibleTickets(tickets)
  const uniqueRoiTickets = uniqBy(roiTickets, ticketUnitKey)
  const eligibleAssets = uniqueRoiTickets.length
  const reviewedAssets = countUniqueUnits(tickets)
  const completeAssets = countUniqueUnits(
    tickets.filter(isTicketCompleteForCalculator)
  )
  const inProgressAssets = countUniqueUnits(
    tickets.filter(isTicketInProgressForCalculator)
  )
  const notStartedAssets = Math.max(
    0,
    reviewedAssets - completeAssets - inProgressAssets
  )

  const assetCostRows = uniqueRoiTickets.map((ticket) =>
    resolveNiiRoiAssetCostInputs(ticket, assumptions)
  )
  const equipment = assetCostRows.reduce(
    (sum, costs) => sum + costs.equipmentPrepMobDemobAedPerAsset,
    0
  )
  const inspection = assetCostRows.reduce(
    (sum, costs) =>
      sum +
      (costs.traditionalInspectionAedPerAsset - costs.niiInspectionAedPerAsset),
    0
  )
  const scaffolding = assetCostRows.reduce(
    (sum, costs) => sum + costs.scaffoldingAedPerAsset,
    0
  )
  const hse =
    eligibleAssets *
    assumptions.traditionalInspectionTeamPerAsset *
    assumptions.hseIncidentProbPerPersonPerTar *
    assumptions.hseDownsideAed
  const production = assumptions.inspectionOnCriticalPath
    ? assumptions.tarDurationDays *
      (assumptions.criticalPathReductionPercent / 100) *
      assumptions.dailyProductionAed
    : 0
  // TAR-level saving only counts for turnarounds actually in the NII scope.
  const extension =
    eligibleAssets > 0 ? computeExtensionAvoidanceSaving(assumptions) : 0
  const total =
    equipment + inspection + scaffolding + hse + production + extension

  return {
    siteId: tickets[0] ? ticketSiteKey(tickets[0]) : UNKNOWN_SITE_KEY,
    reviewedAssets,
    eligibleAssets,
    notStartedAssets,
    inProgressAssets,
    completeAssets,
    savingPerEligibleAsset: eligibleAssets > 0 ? total / eligibleAssets : 0,
    assumptions,
    breakdown: {
      equipment,
      inspection,
      scaffolding,
      hse,
      production,
      extension,
      total,
    },
  }
}

export function aggregateNiiCalculatorSavings(
  tickets: ReadonlyArray<LibraTicket>,
  tarRoiBySite: SiteRoiMap
): NiiCalculatorAggregate {
  const globalStored = tarRoiBySite?.[TAR_ROI_GLOBAL_STORAGE_KEY]
  const bySiteGrouped = groupBy(tickets, ticketSiteKey)
  const bySite = Object.entries(bySiteGrouped)
    .flatMap(([siteId, siteTickets]) => {
      if (!siteTickets?.length) return []
      const siteStored =
        siteId === UNKNOWN_SITE_KEY ? undefined : tarRoiBySite?.[siteId]
      return [
        computeNiiCalculatorSavingsForSite(
          siteTickets,
          mergeNiiCalculatorAssumptionsForSite(globalStored, siteStored)
        ),
      ]
    })
    .toSorted((a, b) => a.siteId.localeCompare(b.siteId))

  const byCategory = emptyCalculatorBreakdown()
  let reviewedAssets = 0
  let eligibleAssets = 0
  let notStartedAssets = 0
  let inProgressAssets = 0
  let completeAssets = 0

  for (const site of bySite) {
    byCategory.equipment += site.breakdown.equipment
    byCategory.inspection += site.breakdown.inspection
    byCategory.scaffolding += site.breakdown.scaffolding
    byCategory.hse += site.breakdown.hse
    byCategory.production += site.breakdown.production
    byCategory.extension += site.breakdown.extension
    reviewedAssets += site.reviewedAssets
    eligibleAssets += site.eligibleAssets
    notStartedAssets += site.notStartedAssets
    inProgressAssets += site.inProgressAssets
    completeAssets += site.completeAssets
  }

  byCategory.total =
    byCategory.equipment +
    byCategory.inspection +
    byCategory.scaffolding +
    byCategory.hse +
    byCategory.production +
    byCategory.extension

  return {
    byCategory,
    reviewedAssets,
    eligibleAssets,
    notStartedAssets,
    inProgressAssets,
    completeAssets,
    savingPerEligibleAsset:
      eligibleAssets > 0 ? byCategory.total / eligibleAssets : 0,
    bySite,
  }
}

export function getDistinctSiteIdsFromTickets(
  tickets: ReadonlyArray<LibraTicket>
): string[] {
  return uniq(
    tickets
      .map((t) => t.tags.site)
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
  )
}
